"use client";

import log from "logbeacon";
import {
  trackGiftSetupStart,
  trackObPaywallPurchaseSuccess,
} from "./onboardingAnalytics";
import { trackEvent } from "@/src/utils/tracking";
import { openOnboardingPaywall } from "@/src/components/ChatOnboarding/openOnboardingPaywall";
import type { OnboardingPaywallOutcome } from "./onboardingV3Types";
import {
  getOnboardingV3Session,
  patchOnboardingV3Session,
} from "./onboardingV3SessionStorage";
import { runOnboardingPlanPreviewRequest } from "./onboardingV3Api";
import { PLAN_DIAGNOSIS_SOURCE } from "@/src/hooks/plan/planDiagnosisSource";
import type { OpenPlanResumeDraftGoalChatParams } from "@/src/hooks/plan/usePlanGoalChatOpen";
import { startOnboardingHandoffAfterDismiss } from "./handoff/startOnboardingHandoff";

export type OpenPlanResumeDraftGoalChat = (
  params: OpenPlanResumeDraftGoalChatParams,
) => void;

export interface ResolveOnboardingFirstPaywallDeps {
  isPC: boolean;
  /** Active paid member — Unlock skips paywall and resumes draft like gift CTA. */
  isMember?: boolean;
  chatApi: Parameters<typeof runOnboardingPlanPreviewRequest>[0];
  requestHeaders: Record<string, string>;
  openPlanResumeDraftGoalChat: OpenPlanResumeDraftGoalChat;
  onMissingDraftId?: () => void;
}

const ensurePlanPreview = async (
  deps: ResolveOnboardingFirstPaywallDeps,
): Promise<boolean> => {
  const session = getOnboardingV3Session();
  if (!session?.previewId) {
    deps.onMissingDraftId?.();
    return false;
  }
  if (session.planPreview?.draftId) {
    return true;
  }
  try {
    const planPreview = await runOnboardingPlanPreviewRequest(
      deps.chatApi,
      deps.requestHeaders,
      session.previewId,
    );
    patchOnboardingV3Session({ planPreview });
    return Boolean(planPreview.draftId);
  } catch (error) {
    log.warn("[onboarding] plan-preview before paywall failed", error);
    deps.onMissingDraftId?.();
    return false;
  }
};

/**
 * Same handoff as「Paywall – Free plan offer」→「Build my plan free」.
 */
export const resumeOnboardingPlanDraftLikeFreeOffer = async (
  deps: ResolveOnboardingFirstPaywallDeps,
): Promise<void> => {
  trackGiftSetupStart();

  const ready = await ensurePlanPreview(deps);
  const session = getOnboardingV3Session();
  const draftId = session?.planPreview?.draftId;

  if (!ready || !draftId) {
    deps.onMissingDraftId?.();
    return;
  }

  deps.openPlanResumeDraftGoalChat({
    draftGoalId: draftId,
    draftTitle: session?.planPreview?.title,
    summary: session?.planPreview?.summary ?? "",
    source: PLAN_DIAGNOSIS_SOURCE.ONBOARDING_V3_0_1,
  });
};

export const openOnboardingFirstPaywall = async (
  isPC: boolean,
  options?: { onWebCheckoutRedirect?: () => void },
): Promise<OnboardingPaywallOutcome> => {
  const result = await openOnboardingPaywall(isPC, {
    onWebCheckoutRedirect: options?.onWebCheckoutRedirect,
  });
  return result === "purchased" ? "purchased" : "dismissed";
};

/**
 * Plan Overview「Unlock my full plan」：
 * - 会员 → 直接走 Free plan offer「Build my plan free」承接
 * - 非会员 → 第一次 Paywall，再按 outcome 解析
 */
export const resolveOnboardingPlanUnlock = async (
  deps: ResolveOnboardingFirstPaywallDeps,
): Promise<void> => {
  if (deps.isMember) {
    await resumeOnboardingPlanDraftLikeFreeOffer(deps);
    return;
  }

  let redirectedToWebCheckout = false;
  const outcome = await openOnboardingFirstPaywall(deps.isPC, {
    onWebCheckoutRedirect: () => {
      redirectedToWebCheckout = true;
    },
  });
  if (redirectedToWebCheckout) {
    return;
  }
  await resolveOnboardingFirstPaywallOutcome(outcome, deps);
};

export const resolveOnboardingFirstPaywallOutcome = async (
  outcome: OnboardingPaywallOutcome,
  deps: ResolveOnboardingFirstPaywallDeps,
): Promise<void> => {
  const ready = await ensurePlanPreview(deps);
  const session = getOnboardingV3Session();
  const draftId = session?.planPreview?.draftId;

  if (outcome === "purchased") {
    if (!ready || !draftId) {
      deps.onMissingDraftId?.();
      return;
    }
    trackObPaywallPurchaseSuccess("onboarding");
    deps.openPlanResumeDraftGoalChat({
      draftGoalId: draftId,
      draftTitle: session?.planPreview?.title,
      summary: session?.planPreview?.summary ?? "",
      source: PLAN_DIAGNOSIS_SOURCE.ONBOARDING_V3_0_1,
    });
    return;
  }

  trackEvent("ob_paywall_dismiss", { source: "onboarding" });

  // V3.0.3: replace Gift Set Goal with Ed coachmark on Plan·Activity
  startOnboardingHandoffAfterDismiss();
};
